import React, { useState, useRef } from "react";
import {
  IconButton,
  Box,
  Typography,
  Grid,
  Tooltip,
  useMediaQuery,
  useTheme,
  Popover,
  Divider,
} from "@mui/material";
import { X as CloseIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import icons from "assets/icons";
import { MailPlus, Layout, Lightbulb } from "lucide-react";
import { headerIconButtonSx, V2 } from "layout/Dashboard/Header/headerV2Tokens";
import { brand } from "brand/theme";

const tileSx = (compact) => ({
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  p: compact ? 1 : 1.25,
  borderRadius: 0,
  border: `1px solid ${V2.line}`,
  bgcolor: V2.surfaceSolid,
  transition: "border-color 0.15s ease, background 0.15s ease",
  minHeight: compact ? 84 : 96,
  fontFamily: V2.fontBody,
  "&:hover": {
    bgcolor: V2.accentSoft,
    borderColor: V2.accentBorder,
  },
});

const iconWrapSx = (compact) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  mb: compact ? 0.75 : 1,
  bgcolor: "transparent",
  border: 0,
  color: V2.ink,
  "& img": {
    width: compact ? 26 : 30,
    height: compact ? 26 : 30,
  },
});

const QuickNavButton = ({ isAdmin = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumDevice = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const buttonRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateTo = (path) => {
    navigate(path);
    handleClose();
  };

  const renderIcon = (iconSrc) => (
    <Box
      component="img"
      src={iconSrc}
      alt=""
      sx={{
        width: 30,
        height: 30,
        filter: brand.iconFilters.ink,
      }}
    />
  );

  const renderLucideIcon = (IconComponent, color = V2.ink, size = 28) => (
    <IconComponent size={size} color={color} strokeWidth={1.75} />
  );

  const standardNavCards = [
    {
      title: "Experts",
      iconComponent: renderIcon(icons.expert),
      path: "/experts",
    },
    {
      title: "Institutions",
      iconComponent: renderIcon(icons.institutions),
      path: "/institutions",
    },
    {
      title: "Newsletters",
      iconComponent: renderIcon(icons.news),
      path: "/newsletter",
    },
    {
      title: "Contribuer",
      iconComponent: renderLucideIcon(Lightbulb, V2.ink),
      path: "/contribuer",
    },
  ];

  const adminNavCards = [
    {
      title: "Abonné",
      iconComponent: renderLucideIcon(MailPlus, V2.accent),
      path: "/admin/newsletters",
      bgColor: V2.accentSoft,
    },
    {
      title: "Bannières publicitaires",
      iconComponent: renderLucideIcon(Layout, brand.colors.critical),
      path: "/admin/list-banners",
      bgColor: "rgba(194, 65, 12, 0.1)",
    },
  ];

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Accès rapide">
        <IconButton
          ref={buttonRef}
          variant="text"
          size="large"
          onClick={handleOpen}
          color="secondary"
          sx={headerIconButtonSx(open)}
        >
          <Box
            component="img"
            src={icons.menuSquare}
            alt="Menu"
            sx={{
              width: 20,
              height: 20,
              filter: open ? brand.iconFilters.accent : brand.iconFilters.ink,
            }}
          />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        disableRestoreFocus
        disableEnforceFocus
        PaperProps={{
          elevation: 0,
          sx: {
            width: isSmallDevice ? "90vw" : isMediumDevice ? 320 : 350,
            maxWidth: isSmallDevice ? "90vw" : 400,
            borderRadius: 0,
            m: 0,
            p: isSmallDevice ? 1.5 : 2,
            bgcolor: V2.surfaceSolid,
            boxShadow: "0 12px 40px rgba(15, 23, 32, 0.12)",
            overflow: "visible",
            mt: 0.75,
            position: "relative",
            zIndex: theme.zIndex.popover + 1,
            border: `1px solid ${V2.line}`,
            fontFamily: V2.fontBody,
          },
        }}
        style={{ zIndex: theme.zIndex.popover + 1 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
            pr: 0.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: V2.fontDisplay,
              fontWeight: 600,
              fontSize: "1.05rem",
              color: V2.ink,
              letterSpacing: "-0.02em",
            }}
          >
            Accès rapide
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Fermer"
            sx={{
              p: 0.5,
              borderRadius: 0,
              border: `1px solid ${V2.line}`,
              color: V2.ink,
              "&:hover": {
                bgcolor: V2.accentSoft,
                borderColor: V2.accentBorder,
                color: V2.accent,
              },
            }}
          >
            <CloseIcon size={16} strokeWidth={2} />
          </IconButton>
        </Box>

        <Grid container spacing={1}>
          {standardNavCards.map((card) => (
            <Grid item xs={6} key={card.title}>
              <Box
                onClick={() => navigateTo(card.path)}
                sx={tileSx(isSmallDevice)}
              >
                <Box sx={iconWrapSx(isSmallDevice)}>
                  {card.iconComponent}
                </Box>
                <Typography
                  align="center"
                  sx={{
                    fontSize: isSmallDevice ? "0.75rem" : "0.875rem",
                    lineHeight: 1.25,
                    fontFamily: V2.fontBody,
                    fontWeight: 600,
                    color: V2.ink,
                  }}
                >
                  {card.title}
                </Typography>
              </Box>
            </Grid>
          ))}

          {isAdmin && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5, borderColor: V2.line }} />
              </Grid>
              {adminNavCards.map((card) => (
                <Grid item xs={6} key={card.title}>
                  <Box
                    onClick={() => navigateTo(card.path)}
                    sx={tileSx(isSmallDevice)}
                  >
                    <Box sx={iconWrapSx(isSmallDevice)}>
                      {card.iconComponent}
                    </Box>
                    <Typography
                      align="center"
                      sx={{
                        fontSize: isSmallDevice ? "0.75rem" : "0.875rem",
                        lineHeight: 1.25,
                        fontFamily: V2.fontBody,
                        fontWeight: 600,
                        color: V2.ink,
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Popover>
    </>
  );
};

export default QuickNavButton;
